import { Avatar } from '@/components/ui/avatar'
import { Badge, type BadgeProps } from '@/components/ui/badge'
import { ChannelMark } from '@/components/channel-icons'
import { formatDateRange, getChannelLabel, getChannelBadgeVariant } from '@/lib/format-utils'
import { getReservationStayDates } from '@/lib/reservation-utils'

/**
 * Shape of the reservation object this header reads - only the fields it
 * actually consumes (mirrors the same Uplisting-sourced shape used by
 * reservation-sidebar.tsx and getReservationStayDates).
 */
interface Reservation {
  id?: string
  reservation_id?: string
  channel?: string
  check_in?: string
  check_out?: string
  external?: {
    uplisting?: {
      number_of_guests?: number | null
      channel?: string
      check_in?: string
      check_out?: string
      attributes?: { check_in?: string; check_out?: string; start_date?: string; end_date?: string }
    }
  } | null
}

interface ThreadHeaderProps {
  reservation?: Reservation | null
  guestDisplayName?: string
  propertyName?: string
}

/**
 * Context strip above the message bubbles: who this thread is with, via
 * which channel, how many guests, and their stay dates. All of this is
 * already fetched by property-messages.jsx (the same reservation the
 * ReservationSidebar renders) - purely presentational, no fetching here.
 *
 * `propertyName` is optional and only meant for a caller with no other
 * property context on screen - the admin portal-wide Inbox (inbox.jsx),
 * where a deep-linked/uncached thread has no corresponding conversation-list
 * row to show it (Codex PR review: an operator reviewing or replying to a
 * message had no way to tell which property it concerned). Omitted by
 * property-messages.jsx, where the page's own header already establishes
 * which property is being viewed.
 */
export function ThreadHeader({ reservation, guestDisplayName, propertyName }: ThreadHeaderProps) {
  const uplistingData = reservation?.external?.uplisting || {}
  const guests = uplistingData.number_of_guests || null
  const channel = reservation?.channel || uplistingData.channel || null
  const { checkIn, checkOut } = getReservationStayDates(reservation)
  const dateRange = checkIn && checkOut ? formatDateRange(checkIn, checkOut) : null

  return (
    <div
      className="flex items-center gap-3 border-b border-border px-4 py-3"
      data-testid="thread-header"
    >
      <Avatar name={guestDisplayName} seed={reservation?.id || reservation?.reservation_id} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-text">{guestDisplayName}</div>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-textMuted">
          {propertyName && <span className="font-medium text-text">{propertyName}</span>}
          {channel && (
            <Badge
              variant={getChannelBadgeVariant(channel) as BadgeProps['variant']}
              className="gap-1 text-xs"
            >
              <ChannelMark channel={channel} className="h-3.5 w-3.5" />
              {getChannelLabel(channel)}
            </Badge>
          )}
          {guests && (
            <span>
              {guests} guest{guests !== 1 ? 's' : ''}
            </span>
          )}
          {dateRange && <span>{dateRange}</span>}
        </div>
      </div>
    </div>
  )
}
